import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import '@/App.css';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/context/AuthContext';
import { GiulioProvider } from '@/context/GiulioContext';
import { ThemeProvider } from '@/context/ThemeContext';
import Navbar from '@/components/Navbar';
import BetaBanner from '@/components/BetaBanner';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import AiFloatingButton from '@/components/AiFloatingButton';
import CookieBanner from '@/components/CookieBanner';
import PwaInstallPrompt from '@/components/PwaInstallPrompt';
import { GlassAmbient } from '@/components/GlassAmbient';
import ScrollToTop from '@/components/ScrollToTop';
import { usePageviewTracking } from '@/lib/analytics';

import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import Explore from '@/pages/Explore';
import MapPage from '@/pages/MapPage';
import ListingDetail from '@/pages/ListingDetail';
import PublishListing from '@/pages/PublishListing';
import Dashboard from '@/pages/Dashboard';
import Messages from '@/pages/Messages';
import Batches from '@/pages/Batches';
import BatchDetail from '@/pages/BatchDetail';
import PublishBatch from '@/pages/PublishBatch';
import SuggestedBatches from '@/pages/SuggestedBatches';
import HowItWorks from '@/pages/HowItWorks';
import Preferences from '@/pages/Preferences';
import Suggested from '@/pages/Suggested';
import AiAssistant from '@/pages/AiAssistant';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import NewsletterConfirm from '@/pages/NewsletterConfirm';
import NewsletterUnsubscribe from '@/pages/NewsletterUnsubscribe';
import ChiSiamo from '@/pages/ChiSiamo';
import Contatti from '@/pages/Contatti';
import CookiePolicy from '@/pages/CookiePolicy';
import Promo from '@/pages/Promo';
import LandReport from '@/pages/LandReport';
import Proprietari from '@/pages/landing/Proprietari';
import Investitori from '@/pages/landing/Investitori';
import Agricoltori from '@/pages/landing/Agricoltori';
import StimaQuintali from '@/pages/StimaQuintali';
import ConfirmEmail from '@/pages/ConfirmEmail';
import MascotPreview from '@/pages/MascotPreview';
import Mascot from '@/components/Mascot';

import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminListings from '@/pages/admin/AdminListings';
import AdminBatches from '@/pages/admin/AdminBatches';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminReports from '@/pages/admin/AdminReports';
import AdminVerifications from '@/pages/admin/AdminVerifications';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminAuditLog from '@/pages/admin/AdminAuditLog';

// Stale-tab redirect: se l'utente non apre il sito da più di STALE_MINUTES,
// al prossimo caricamento viene riportato alla home. Mantiene aperte le pagine
// di autenticazione/callback per non interrompere flussi critici.
const STALE_MINUTES = 30;
const STALE_KEY = 'agn_last_seen';
const STALE_EXEMPT = [
  '/',
  '/login',
  '/registrati',
  '/password/dimenticata',
  '/password/reset',
  '/auth/callback',
];

// Modulo-scope: calcolato UNA SOLA volta al caricamento del bundle (hard reload).
// Non viene ricalcolato tra navigazioni client-side, perciò non interferisce con il
// normale uso dell'app dopo il primo render.
const INITIAL_STALE = (() => {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const exempt = STALE_EXEMPT.some(p => path === p || path.startsWith(p + '/'));
    if (exempt) return false;
    const last = parseInt(localStorage.getItem(STALE_KEY) || '0', 10);
    if (!last) return false;
    return (Date.now() - last) > STALE_MINUTES * 60 * 1000;
  } catch { return false; }
})();

function useStaleTabRefresh(pathname) {
  // Aggiorna il timestamp su ogni cambio di route (anche al primo mount).
  useEffect(() => {
    try { localStorage.setItem(STALE_KEY, String(Date.now())); } catch {}
  }, [pathname]);

  // Quando la tab torna visibile, se è passato troppo tempo → reload della home.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const last = parseInt(localStorage.getItem(STALE_KEY) || '0', 10);
        const stale = last && (Date.now() - last) > STALE_MINUTES * 60 * 1000;
        const path = window.location.pathname;
        const exempt = STALE_EXEMPT.some(p => path === p || path.startsWith(p + '/'));
        if (stale && !exempt) {
          // Hard navigation per resettare lo stato dell'app e assicurarsi che
          // vengano ricaricati dati freschi (listings, auth, ecc.).
          window.location.replace('/');
        } else {
          localStorage.setItem(STALE_KEY, String(Date.now()));
        }
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
}


function AppRouter() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  usePageviewTracking();
  useStaleTabRefresh(location.pathname);
  // Se all'avvio del bundle la tab era "stale", redirect alla home (sincrono).
  if (INITIAL_STALE && !location.hash?.includes('session_id=')) {
    return <Navigate to="/" replace />;
  }
  // Emergent OAuth returns to /dashboard#session_id=... — intercept BEFORE routing.
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  const pageKey = location.pathname.split('/')[1] || 'home';
  return (
    <>
      <ScrollToTop />
      <GlassAmbient />
      {!isAdminRoute && <BetaBanner />}
      {!isAdminRoute && <Navbar />}
      <div key={`warp-${location.pathname}`} className="route-warp" aria-hidden data-testid="route-warp"/>
      <main className="pb-24 sm:pb-8">
        <div key={pageKey} className="page-enter" onAnimationEnd={(e) => { if (e.target === e.currentTarget) e.currentTarget.classList.remove('page-enter'); }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registrati" element={<Register />} />
          <Route path="/password/dimenticata" element={<ForgotPassword />} />
          <Route path="/password/reset/:token" element={<ResetPassword />} />
          <Route path="/conferma-email/:token" element={<ConfirmEmail />} />
          <Route path="/esplora" element={<Explore />} />
          <Route path="/mappa" element={<MapPage />} />
          <Route path="/come-funziona" element={<HowItWorks />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/termini" element={<Terms />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/cookie" element={<CookiePolicy />} />
          <Route path="/newsletter/conferma" element={<NewsletterConfirm />} />
          <Route path="/newsletter/disiscrivi" element={<NewsletterUnsubscribe />} />
          <Route path="/scopri-agrinexus" element={<Promo />} />
          <Route path="/spot" element={<Promo />} />
          <Route path="/proprietari" element={<Proprietari />} />
          <Route path="/investitori" element={<Investitori />} />
          <Route path="/agricoltori" element={<Agricoltori />} />
          <Route path="/stima-quintali" element={<StimaQuintali />} />
          <Route path="/_mascot" element={<MascotPreview />} />
          <Route path="/terreno/:id" element={<ListingDetail />} />
          <Route path="/terreno/:id/report" element={<ProtectedRoute><LandReport /></ProtectedRoute>} />
          <Route path="/pubblica" element={<ProtectedRoute><PublishListing /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/preferenze" element={<ProtectedRoute><Preferences /></ProtectedRoute>} />
          <Route path="/suggeriti" element={<ProtectedRoute><Suggested /></ProtectedRoute>} />
          <Route path="/assistente" element={<ProtectedRoute><AiAssistant /></ProtectedRoute>} />
          <Route path="/messaggi" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messaggi/:cid" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/partite" element={<Batches />} />
          <Route path="/partite/pubblica" element={<ProtectedRoute><PublishBatch /></ProtectedRoute>} />
          <Route path="/partite/suggerite" element={<ProtectedRoute><SuggestedBatches /></ProtectedRoute>} />
          <Route path="/partite/:id" element={<BatchDetail />} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="listings" element={<AdminListings />} />
            <Route path="batches" element={<AdminBatches />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="verifications" element={<AdminVerifications />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
          </Route>
          <Route path="*" element={
            <div className="min-h-[70vh] flex items-center justify-center px-6">
              <div className="max-w-md text-center">
                <div className="flex justify-center mb-2">
                  <Mascot pose="lost" size={180}/>
                </div>
                <div className="font-display text-7xl md:text-8xl tracking-tighter text-terracotta">404</div>
                <h1 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">Pagina non trovata</h1>
                <p className="mt-3 text-sm text-muted-foreground">Giulio si è perso tra i filari… La pagina che stai cercando non esiste o è stata spostata.</p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition" data-testid="404-home-btn">Torna alla home</Link>
                  <Link to="/esplora" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-sm font-medium hover:bg-secondary transition" data-testid="404-explore-btn">Esplora terreni</Link>
                </div>
              </div>
            </div>
          } />
        </Routes>
        </div>
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <AiFloatingButton />}
      {!isAdminRoute && <CookieBanner />}
      {!isAdminRoute && <PwaInstallPrompt />}
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <GiulioProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </GiulioProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
